<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <DropdownSection
    class="chatmigo-profile-status-section [&>ul]:overflow-visible"
  >
    <div class="grid gap-0">
      <DropdownItem preserve-open class="chatmigo-profile-status-row gap-1">
        <div class="chatmigo-profile-status-panel">
          <div class="chatmigo-profile-toggle-row">
            <div class="chatmigo-profile-toggle-copy">
              <span class="chatmigo-profile-status-label">
                {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
              </span>
              <span class="chatmigo-profile-status-help">
                {{ $t('SIDEBAR_ITEMS.CHANGE_AVAILABILITY_STATUS') }}
              </span>
            </div>
            <DropdownContainer class="shrink-0">
              <template #trigger="{ toggle }">
                <Button
                  size="sm"
                  color="slate"
                  variant="outline"
                  icon="i-lucide-chevron-down"
                  trailing-icon
                  class="chatmigo-profile-status-trigger"
                  @click="toggle"
                >
                  <div class="flex gap-1 items-center min-w-0 text-sm">
                    <div class="p-1 flex-shrink-0">
                      <div
                        class="size-2 rounded-sm"
                        :class="activeStatus.color"
                      />
                    </div>
                    <span class="truncate max-w-[7rem]">
                      {{ activeStatus.label }}
                    </span>
                  </div>
                </Button>
              </template>
              <DropdownBody class="min-w-32 z-20">
                <DropdownItem
                  v-for="status in availabilityStatuses"
                  :key="status.value"
                  :label="status.label"
                  :icon="status.icon"
                  class="cursor-pointer"
                  @click="changeAvailabilityStatus(status.value)"
                />
              </DropdownBody>
            </DropdownContainer>
          </div>
        </div>
      </DropdownItem>
      <DropdownItem class="chatmigo-profile-status-row">
        <div class="chatmigo-profile-status-panel">
          <div class="chatmigo-profile-toggle-row">
            <div class="chatmigo-profile-toggle-copy">
              <span class="chatmigo-profile-status-label">
                {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
              </span>
              <span class="chatmigo-profile-status-help">
                {{ $t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT') }}
              </span>
            </div>
            <ToggleSwitch v-model="autoOfflineToggle" />
          </div>
        </div>
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
